import { supabase } from '../lib/supabase';
import { DeliveryQuote } from '../types/delivery';
import { createClient } from '@supabase/supabase-js';
import { DELIVERY_CONFIG, DeliveryHelpers } from '../config/deliveryConfig';

// Create a Supabase client with the service role key to bypass RLS
const supabaseAdmin = supabase;

interface DeliveryAddress {
  street_address: string[];
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

interface ManifestItem {
  name: string;
  quantity: number;
}

interface DeliveryVerification {
  signature?: boolean;
  signature_requirement?: {
    enabled: boolean;
    collect_signer_name: boolean;
    collect_signer_relationship: boolean;
  };
  barcodes?: Array<{
    value: string;
    type: 'CODE39' | 'CODE128' | 'QR' | 'CODE39_FULL_ASCII';
  }>;
  identification?: {
    min_age: number;
  };
  picture?: boolean;
  pincode?: {
    enabled: boolean;
  };
}

interface DeliveryQuoteRequest {
  pickup_address: DeliveryAddress;
  dropoff_address: DeliveryAddress;
  pickup_latitude: number;
  pickup_longitude: number;
  dropoff_latitude: number;
  dropoff_longitude: number;
  manifest_items: ManifestItem[];
  pickup_ready_dt?: string;
  pickup_deadline_dt?: string;
  dropoff_ready_dt?: string;
  dropoff_deadline_dt?: string;
  pickup_phone_number?: string;
  dropoff_phone_number?: string;
}

interface RoboCourierSpecification {
  mode: 'auto' | 'custom';
  enroute_for_pickup_at?: string; // ISO 8601 timestamp
  pickup_imminent_at?: string; // ISO 8601 timestamp
  pickup_at?: string; // ISO 8601 timestamp
  dropoff_imminent_at?: string; // ISO 8601 timestamp
  dropoff_at?: string; // ISO 8601 timestamp
}

interface TestSpecifications {
  robo_courier_specification: RoboCourierSpecification;
}

interface CreateDeliveryRequest {
  quote_id: string;
  pickup_address: DeliveryAddress;
  dropoff_address: DeliveryAddress;
  pickup_name: string;
  pickup_phone_number: string;
  dropoff_name: string;
  dropoff_phone_number: string;
  manifest_items: ManifestItem[];
  pickup_latitude: number;
  pickup_longitude: number;
  dropoff_latitude: number;
  dropoff_longitude: number;
  pickup_business_name?: string;
  pickup_notes?: string;
  pickup_verification?: DeliveryVerification;
  dropoff_business_name?: string;
  dropoff_notes?: string;
  dropoff_seller_notes?: string;
  dropoff_verification?: DeliveryVerification;
  deliverable_action?: 'deliverable_action_meet_at_door' | 'leave_at_door';
  manifest_reference?: string;
  undeliverable_action?: 'leave_at_door' | 'return';
  pickup_ready_dt: string;
  pickup_deadline_dt: string;
  dropoff_ready_dt: string;
  dropoff_deadline_dt: string;
  requires_dropoff_signature?: boolean;
  requires_id?: boolean;
  tip?: number;
  idempotency_key?: string;
  return_verification?: DeliveryVerification;
  test_specifications?: TestSpecifications; // For robo courier testing
}

interface UberAuthResponse {
  access_token: string;
  expires_in: number;
}

interface RefundRequest {
  delivery_id: string;
  reason: string;
  refund_items: Array<{
    name: string;
    quantity: number;
    amount: number;
  }>;
  party_at_fault: 'UBER' | 'PARTNER';
}

class UberDirectService {
  // Token management removed - all operations use edge functions which handle tokens internally
  // This ensures consistent sandbox/production configuration and better rate limiting
  private readonly clientId: string;
  private readonly customerId: string;

  constructor() {
    // Use EXPO_PUBLIC_ prefix for environment variables in React Native
    // Note: clientSecret removed - all API calls go through edge functions which handle auth
    this.clientId = process.env.EXPO_PUBLIC_UBER_CLIENT_ID || '';
    this.customerId = process.env.EXPO_PUBLIC_UBER_CUSTOMER_ID || '';

    if (!this.clientId || !this.customerId) {
      console.error('Uber Direct credentials not properly configured:', {
        hasClientId: !!this.clientId,
        hasCustomerId: !!this.customerId
      });
    }
  }

  // NOTE: getAccessToken() has been removed - all operations now use edge functions
  // which handle token management internally with better rate limiting and sandbox/production support.
  // Edge functions use uberAuth.ts which has retry logic and proper sandbox configuration.

  private validateQuoteResponse(response: any): DeliveryQuote {
    if (!response) {
      throw new Error('No response from Uber Direct API');
    }

    // Validate and transform the response to match DeliveryQuote interface
    const quote: DeliveryQuote = {
      quote_id: response.id,
      fee: response.fee || 0,
      currency: response.currency || 'USD',
      currency_type: response.currency_type || 'USD',
      dropoff_eta: response.dropoff_eta || new Date(Date.now() + DELIVERY_CONFIG.timing.DEFAULT_DROPOFF_ETA_OFFSET_MS).toISOString(),
      duration: response.duration || 0,
      pickup_duration: response.pickup_duration || 0,
      dropoff_deadline: response.dropoff_deadline || new Date(Date.now() + DELIVERY_CONFIG.timing.DEFAULT_DROPOFF_DEADLINE_OFFSET_MS).toISOString(),
      expires: response.expires || new Date(Date.now() + DELIVERY_CONFIG.timing.QUOTE_EXPIRATION_OFFSET_MS).toISOString(),
      created: response.created || new Date().toISOString(),
      kind: response.kind || 'delivery_quote',
      id: response.id,
    };

    return quote;
  }

  async getDeliveryQuote(request: DeliveryQuoteRequest): Promise<DeliveryQuote> {
    if (!this.customerId) {
      throw new Error('Uber Direct customer ID not configured');
    }

    try {
      // Use edge functions instead of calling Uber API directly
      // Edge function handles token management internally
      const { data, error } = await supabase.functions.invoke('uber-quote', {
        body: {
          pickup_address: request.pickup_address,
          dropoff_address: request.dropoff_address,
          pickup_latitude: request.pickup_latitude,
          pickup_longitude: request.pickup_longitude,
          dropoff_latitude: request.dropoff_latitude,
          dropoff_longitude: request.dropoff_longitude
        }
      });

      if (error) {
        // When edge function returns non-2xx, data may still contain error details
        let errorMessage = error.message || 'Failed to get delivery quote';
        
        // Log full error structure for debugging
        console.error('[UberDirectService] Edge function error:', {
          errorMessage: error.message,
          errorType: error.constructor?.name,
          hasData: !!data,
          dataKeys: data ? Object.keys(data) : [],
          errorKeys: Object.keys(error),
        });
        
        // Check if data contains error details (edge function may return error in response body)
        if (data) {
          console.log('[UberDirectService] Error response data:', JSON.stringify(data, null, 2));
          
          if (data.error === 'rate_limited') {
            errorMessage = `Rate limited by Uber API. ${data.message || 'Please wait before retrying.'}`;
          } else if (data.error) {
            errorMessage = data.message || data.error || errorMessage;
          } else if (data.message) {
            errorMessage = data.message;
          }
        }
        
        // Try to extract from error object structure (various possible formats)
        if (errorMessage.includes('Edge Function returned a non-2xx status code')) {
          // Check if error has context with response data
          const errorData = (error as any)?.context?.response?.data || 
                           (error as any)?.data || 
                           (error as any)?.response?.data;
          
          if (errorData) {
            if (errorData.error === 'rate_limited') {
              errorMessage = `Rate limited by Uber API. ${errorData.message || 'Please wait before retrying.'}`;
            } else if (errorData.error) {
              errorMessage = errorData.message || errorData.error || errorMessage;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        }
        
        // Check for rate limiting specifically in error message
        if (errorMessage.includes('429') || errorMessage.includes('rate') || errorMessage.includes('Rate limited')) {
          const rateLimitMessage = data?.message || 'Please wait a moment and try again.';
          throw new Error(`Rate limited: ${rateLimitMessage}`);
        }
        
        throw new Error(errorMessage);
      }

      // Check if response contains an error even if no error object
      if (data?.error) {
        const errorMessage = data.error === 'rate_limited'
          ? `Rate limited by Uber API. ${data.message || 'Please wait before retrying.'}`
          : data.message || data.error || 'Failed to get delivery quote';
        throw new Error(errorMessage);
      }

      console.log('Quote response from edge function:', JSON.stringify(data, null, 2));
      return this.validateQuoteResponse(data);
    } catch (error) {
      console.error('Error getting delivery quote:', error);
      throw error;
    }
  }

  async createDelivery(request: CreateDeliveryRequest): Promise<{delivery_id: string; tracking_url: string}> {
    if (!this.customerId) {
      throw new Error('Customer ID not set');
    }

    try {
      // Format addresses exactly as Uber expects
      // Edge function handles token management internally
      const pickupAddressStr = JSON.stringify({
        street_address: Array.isArray(request.pickup_address.street_address)
          ? request.pickup_address.street_address
          : [request.pickup_address.street_address],
        city: request.pickup_address.city,
        state: request.pickup_address.state,
        zip_code: request.pickup_address.zip_code,
        country: request.pickup_address.country
      });

      const dropoffAddressStr = JSON.stringify({
        street_address: Array.isArray(request.dropoff_address.street_address)
          ? request.dropoff_address.street_address
          : [request.dropoff_address.street_address],
        city: request.dropoff_address.city,
        state: request.dropoff_address.state,
        zip_code: request.dropoff_address.zip_code,
        country: request.dropoff_address.country
      });

      // Format the request according to Uber Direct API specifications
      const formattedRequest = {
        quote_id: request.quote_id,
        pickup_address: pickupAddressStr,
        dropoff_address: dropoffAddressStr,
        pickup_name: request.pickup_name,
        pickup_phone_number: request.pickup_phone_number,
        dropoff_name: request.dropoff_name,
        dropoff_phone_number: request.dropoff_phone_number,
        manifest_items: request.manifest_items.map(item => ({
          name: item.name,
          quantity: item.quantity,
        })),
        pickup_latitude: request.pickup_latitude,
        pickup_longitude: request.pickup_longitude,
        dropoff_latitude: request.dropoff_latitude,
        dropoff_longitude: request.dropoff_longitude,
        pickup_business_name: request.pickup_business_name,
        pickup_notes: request.pickup_notes,
        pickup_verification: request.pickup_verification,
        dropoff_business_name: request.dropoff_business_name,
        dropoff_notes: request.dropoff_notes,
        dropoff_seller_notes: request.dropoff_seller_notes,
        dropoff_verification: request.dropoff_verification,
        deliverable_action: request.deliverable_action,
        manifest_reference: request.manifest_reference,
        undeliverable_action: request.undeliverable_action,
        pickup_ready_dt: request.pickup_ready_dt,
        pickup_deadline_dt: request.pickup_deadline_dt,
        dropoff_ready_dt: request.dropoff_ready_dt,
        dropoff_deadline_dt: request.dropoff_deadline_dt,
        requires_dropoff_signature: request.requires_dropoff_signature,
        requires_id: request.requires_id,
        tip: request.tip,
        idempotency_key: request.idempotency_key,
        return_verification: request.return_verification,
        // Include test_specifications for robo courier if provided
        ...(request.test_specifications ? { test_specifications: request.test_specifications } : {})
      };

      // Use edge function instead of calling Uber API directly
      const { data, error: edgeError } = await supabase.functions.invoke('uber-create-delivery', {
        body: formattedRequest
      });

      if (edgeError) {
        throw new Error(`Create delivery function error: ${edgeError.message}`);
      }

      if (!data || data.error) {
        const errorMessage = data?.error?.message || data?.error || data?.message || 'Failed to create delivery';
        throw new Error(`Uber Direct API Error: ${errorMessage}`);
      }

      // Extract delivery_id and tracking_url from response
      const delivery_id = data.id || data.delivery_id || data.delivery?.id;
      const tracking_url = data.tracking_url || data.tracking?.url || null;

      if (!delivery_id) {
        throw new Error('Uber Direct API Error: No delivery ID in response');
      }

      return {
        delivery_id,
        tracking_url
      };
    } catch (error) {
      console.error('Error creating delivery:', error);
      throw error;
    }
  }

  /**
   * Creates a test delivery
   */
  async createTestDelivery(
    pickup_address: DeliveryAddress,
    dropoff_address: DeliveryAddress,
    pickup_latitude: number,
    pickup_longitude: number,
    dropoff_latitude: number,
    dropoff_longitude: number,
    manifest_items: ManifestItem[],
    pickup_name: string,
    pickup_phone_number: string,
    dropoff_name: string,
    dropoff_phone_number: string
  ): Promise<{delivery_id: string; tracking_url: string}> {
    try {
      console.log('UberDirectService: Starting test delivery flow');

      // First, get a quote
      const quoteRequest: DeliveryQuoteRequest = {
        pickup_address,
        dropoff_address,
        pickup_latitude,
        pickup_longitude,
        dropoff_latitude,
        dropoff_longitude,
        manifest_items,
        pickup_phone_number,
        dropoff_phone_number
      };
      
      console.log('UberDirectService: Getting quote');
      const quote = await this.getDeliveryQuote(quoteRequest);
      console.log('UberDirectService: Got quote:', quote);
      
      // Then create the delivery
      const deliveryRequest: CreateDeliveryRequest = {
        quote_id: quote.quote_id,
        pickup_address,
        dropoff_address,
        pickup_latitude,
        pickup_longitude,
        dropoff_latitude,
        dropoff_longitude,
        manifest_items,
        pickup_name,
        pickup_phone_number,
        dropoff_name,
        dropoff_phone_number,
        pickup_ready_dt: DeliveryHelpers.createPickupReady(true),
        pickup_deadline_dt: DeliveryHelpers.createPickupDeadline(true),
        dropoff_ready_dt: DeliveryHelpers.createDropoffReady(true),
        dropoff_deadline_dt: DeliveryHelpers.createDropoffDeadline(true)
      };
      
      console.log('UberDirectService: Creating delivery');
      return await this.createDelivery(deliveryRequest);
    } catch (error) {
      console.error('UberDirectService: Test delivery error:', error);
      throw error;
    }
  }

  async requestRefund(request: RefundRequest): Promise<{ refund_id: string }> {
    if (!this.customerId) {
      throw new Error('Uber Direct customer ID not configured');
    }

    try {
      const formattedRequest = {
        delivery_id: request.delivery_id,
        refund_order_items: [{
          reason: request.reason,
          party_at_fault: request.party_at_fault,
          refund_items: request.refund_items.map(item => ({
            title: item.name,
            quantity: item.quantity,
            value: item.amount * 100 // Convert dollars to cents
          }))
        }]
      };

      console.log('UberDirectService: Requesting refund:', {
        delivery_id: request.delivery_id,
        reason: request.reason,
        party_at_fault: request.party_at_fault
      });

      // Use edge function for refund to ensure consistent sandbox/production configuration
      // and avoid rate limiting from direct client calls
      const { data, error: edgeError } = await supabase.functions.invoke('uber-direct', {
        body: {
          action: 'refund',
          ...formattedRequest
        }
      });

      if (edgeError) {
        throw new Error(`Refund function error: ${edgeError.message}`);
      }

      if (!data || data.error) {
        const errorMessage = data?.error?.message || data?.error || data?.message || 'Failed to request refund';
        throw new Error(`Uber Direct API Error: ${errorMessage}`);
      }
      
      // Save refund request to our database
      const { error } = await supabase.from('refund_requests').insert({
        delivery_id: request.delivery_id,
        refund_data: {
          ...formattedRequest,
          refund_id: data.id,
          created_at: new Date().toISOString()
        },
        processed: false
      });

      if (error) {
        console.error('Error saving refund request:', error);
      }

      return { refund_id: data.id || data.refund_id };
    } catch (error) {
      console.error('Error requesting refund:', error);
      throw error;
    }
  }
}

// Helper function to calculate manifest value
function calculateManifestValue(items: Array<{name: string; quantity: number}>): number {
  // Default to minimum value per item if no price available
  return items.reduce((total, item) => total + (item.quantity * DELIVERY_CONFIG.package.DEFAULT_VALUE_CENTS), 0);
}

export const uberDirectService = new UberDirectService(); 